/**
 * FollowupRuleForm — config section of the /campaigns/:id/followup page
 * (Story 7.8).
 *
 * Fields:
 *   - delayHours (decimal 0-168)
 *   - maxFollowups (integer 0-5)
 *   - activeWindow (reuses ActiveHoursInput from campaigns)
 *   - active toggle
 *
 * The parent owns the initial value and the submit handler. This component
 * owns the intermediate form state and surfaces client-side validation on
 * the activeWindow block. The empty-pool guard (AC4) is enforced server-side
 * and shown as a banner above the toggle when disabled.
 */

import { useEffect, useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ActiveHoursInput,
  DEFAULT_ACTIVE_HOURS,
  type ActiveHoursValue,
} from '@/components/campaigns/ActiveHoursInput';

export interface FollowupRuleFormValue {
  delayHours: number;
  maxFollowups: number;
  activeWindow: ActiveHoursValue;
  active: boolean;
}

export const DEFAULT_FOLLOWUP_RULE: FollowupRuleFormValue = {
  delayHours: 2,
  maxFollowups: 1,
  activeWindow: DEFAULT_ACTIVE_HOURS,
  active: false,
};

interface Props {
  initial?: FollowupRuleFormValue;
  /** True when the message pool is empty — disables the `active` toggle. */
  poolIsEmpty: boolean;
  submitting?: boolean;
  onSubmit: (value: FollowupRuleFormValue) => void;
}

export function FollowupRuleForm({ initial, poolIsEmpty, submitting, onSubmit }: Props) {
  const [form, setForm] = useState<FollowupRuleFormValue>(initial ?? DEFAULT_FOLLOWUP_RULE);
  const [activeWindowError, setActiveWindowError] = useState<string | null>(null);

  useEffect(() => {
    if (initial) setForm(initial);
  }, [initial]);

  function patch<K extends keyof FollowupRuleFormValue>(key: K, value: FollowupRuleFormValue[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function canSubmit(): boolean {
    if (activeWindowError) return false;
    if (form.delayHours < 0 || form.delayHours > 168) return false;
    if (!Number.isInteger(form.maxFollowups) || form.maxFollowups < 0 || form.maxFollowups > 5) {
      return false;
    }
    if (form.active && poolIsEmpty) return false;
    return true;
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit()) return;
    onSubmit(form);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="followup-delay-hours">Delay (horas)</Label>
          <Input
            id="followup-delay-hours"
            type="number"
            min={0}
            max={168}
            step={0.5}
            value={form.delayHours}
            disabled={submitting}
            onChange={(e) => patch('delayHours', Number(e.target.value))}
          />
          <p className="text-xs text-muted-foreground">Entre 0 e 168 (7 dias). Aceita decimais.</p>
        </div>
        <div className="space-y-1">
          <Label htmlFor="followup-max-followups">Máx. follow-ups</Label>
          <Input
            id="followup-max-followups"
            type="number"
            min={0}
            max={5}
            step={1}
            value={form.maxFollowups}
            disabled={submitting}
            onChange={(e) => patch('maxFollowups', Number(e.target.value))}
          />
          <p className="text-xs text-muted-foreground">Entre 0 e 5.</p>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Janela ativa</Label>
        <ActiveHoursInput
          value={form.activeWindow}
          onChange={(next) => patch('activeWindow', next)}
          onValidityChange={setActiveWindowError}
          disabled={submitting}
        />
      </div>

      {poolIsEmpty && (
        <div
          role="alert"
          className="rounded-md border border-yellow-500/40 bg-yellow-500/10 p-3 text-xs text-yellow-800 dark:text-yellow-200"
        >
          Adicione ao menos uma mensagem ao pool antes de ativar a regra.
        </div>
      )}

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.active}
          disabled={submitting || poolIsEmpty}
          onChange={(e) => patch('active', e.target.checked)}
          aria-label="Regra ativa"
        />
        Regra ativa
      </label>

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button type="submit" disabled={submitting || !canSubmit()}>
          {submitting ? 'Salvando…' : 'Salvar regra'}
        </Button>
      </div>
    </form>
  );
}

export default FollowupRuleForm;
